
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { api, url, usarCotizadorV2 } from '../env';
import { getObjectWithLowestValue } from '../Utils';
import { clearSourceTrimAccess, getSourceTrimAccess, notifySourceTrimAccessInvalid } from '../Utils/sourceTrimAccess';
import { getAddressForStatePlate, normalizeAddressValue } from '../Utils/statePlateAddress';
import { getZipCode } from '../Views/Cotizador/Steps/Step1/getZipCode';
import { limitesPagoInicial } from '../Utils/pagoInicial';
import { useModal } from '../Context/ModalContext';
import {
    getCarStatuses,
    getCarTypes,
    getCompensations,
    getDeductibles,
    getPackages,
    getPolicyTypes,
    getUdis,
    getUseTypes,
    getSubchannels,
} from "../Views/Cotizador/functions";
import {
    getBrands,
    getDateInDays,
    getDescriptions,
    getInsurances,
    getModels,
    getPaymentConfiguration,
    getTypes,
} from "../Views/Cotizador/functions/rodac";

const DEFAULT_PAYMENT_CONFIGURATIONS = [
    { id: 13, configuration: "24" },
    { id: 14, configuration: "36" },
    { id: 15, configuration: "48" },
    { id: 16, configuration: "60" }
];

const COTIZADOR3_PAYMENT_CONFIGURATIONS = [
    { id: 13, configuration: "24" },
    { id: 14, configuration: "36" },
    { id: 15, configuration: "48" }
];

function paymentConfigurationsFor(isCotizador3) {
    return (isCotizador3 ? COTIZADOR3_PAYMENT_CONFIGURATIONS : DEFAULT_PAYMENT_CONFIGURATIONS)
        .map(item => ({ ...item }));
}

function plazosFor(paymentConfigurations) {
    return {
        currentIndex: 0,
        idarray: paymentConfigurations.map(item => item.id),
        parray: paymentConfigurations.map(item => +item.configuration),
    };
}

function rodacDefaultsFor(isCotizador3) {
    const paymentConfigurations = paymentConfigurationsFor(isCotizador3);

    return {
        ...objectRodac,
        payment_configurations: paymentConfigurations,
        payment_configuration_id: paymentConfigurations[0]?.id ?? null,
        plazosInsurances: [],
        plazosInsurancesFiltered: [],
        plazosInsurancesChanged: [],
        insurances: [],
        insurance: null,
    };
}

const objectRodac = {
    fetch_subchannels: true,
    subchannels: [],
    subchannel_id: null,
    fetch_car_types: true,
    car_types: [],
    car_type_id: null,
    fetch_car_statuses: true,
    car_statuses: [],
    car_status_select: null,
    car_status_id: null,
    fetch_use_types: true,
    use_types: [],
    use_type_id: null,
    fetch_deductibles: true,
    deductibles: [],
    deductible_id: null,
    fetch_policy_types: true,
    policy_types: [],
    policy_type_id: null,
    fetch_udis: false, // IGNORAR LA PETICIÓN, PORQUE TRAE ERROR
    udis: [],
    udi_id: null,
    fetch_packages: true,
    packages: [],
    package_id: null,
    fetch_compensations: true,
    compensations: [],
    compensation_id: null,
    models: [],
    model_id: null,
    brands: [],
    brand: null,
    types: [],
    type: null,
    descriptions: [],
    description_id: null,
    fetch_payment_configurations: false, // Set to false because we have defaults
    payment_configurations: DEFAULT_PAYMENT_CONFIGURATIONS,
    payment_configuration_id: 13,
    fetch_insurances: false,
    fetched_insurances: false,
    plazosInsurances: [],
    plazosInsurancesFiltered: [],
    plazosInsurancesChanged: [],
    insurances: [],
    insurance: null,
    // info
    name: null,
    paterno: null,
    car_type_select: null,
    materno: null,
    amount: null,
    accesorios: null,
    enganche: null,
    zip: null,
    colonia: null,
    municipio: null,
    ciudad: null,
    estado: null,
    estado_placa: null,
    date: getDateInDays(10),
};

// La dirección por placa (BD → respaldo por nombre) vive en Utils/statePlateAddress.
function buildColonyOption(colony, id = 1) {
    const normalized = normalizeAddressValue(colony);

    if (!normalized) {
        return null;
    }

    return {
        id,
        colonia: normalized,
        label: normalized,
    };
}

function translateCarStatus(str) {
    switch (str) {
        case "nuevo":
            return {
                select: {
                    id: 1,
                    status: "NUEVO",
                },
                id: 1,
            };
            break;

        case "seminuevo":
            return {
                select: {
                    id: 2,
                    status: "SEMINUEVO",
                },
                id: 2,
            };
            break;

        default:
            return {
                select: null,
                id: null,
            };
            break;
    }
}

export const useCarCalculator = (internalVariables, setStep, budgetData, id = null, isAdmin = false, budgetSecret = null, budgetId = null, submitEndpointOverride = null) => {

    /*  let budget_id =  */

    // Get URL params immediately
    const urlParams = useParams();
    const isCotizador3 = submitEndpointOverride === 'cotizador3';
    const initialRodac = rodacDefaultsFor(isCotizador3);
    const initialPlazos = plazosFor(initialRodac.payment_configurations);
    const type_coverage_options = [{ id: 1, type_coverage: 'FINANCIERO' }, { id: 2, type_coverage: 'PURO' }];

    const [state, setState] = useState(() => ({
        loader: false,
        resumen: null,
        detalle: null,
        residualValue: {
            id: null,
            percentage: null
        },
        typeCoverage: {
            typeCoverage: null,
            typeCoverageName: {
                id: null,
            },
            id: null,
        },
        typeRegime: {
            id: null,
            typeRegime: null,
            typeRegimeName: {
                id: null,
            }
        },
        typeEngine: {
            id: null,
            typeEngine: null,
            typeEngineName: {
                id: null,
            },
        },
        expectedKm: null,
        netIncome: null,
        plazos: initialPlazos,
        rodac: initialRodac,
    }));
    const [loading, setLoading] = useState(false);
    const [plazoVal, setPlazoVal] = useState(+initialRodac.payment_configurations.find(item => +item.id === +initialRodac.payment_configuration_id)?.configuration);
    const plazoTimer = useRef(null);
    const { openModal } = useModal();
    const [errorsArray, setErrorsArray] = useState([]);
    const [typeRegime, setTypeRegime] = useState(null);
    const [residualValue, setResidualValue] = useState(null)
    const [stateAmple, setStateAmple] = useState(null)
    const [percentageDownPayment, setPercentageDownPayment] = useState({
        min: 0,
        max: 0
    });
    const [coloniesFiltered, setColoniesFiltered] = useState([]);
    const debounceTimeout = useRef(null);
    const dropdownRef = useRef(null);
    const params = urlParams; // Use the urlParams already declared above
    const [minimumLoaded, setMinimumLoaded] = useState(false);
    const previousBudgetData = useRef(null);
    const urlParamLoaded = useRef(false);


    function sumAmountAndAccesories() {
        let n1 = state.rodac.amount;
        let n2 = state.rodac.accesorios;

        if (isNaN(n1) || n1 === null)
            n1 = 0;

        if (isNaN(n2) || n2 === null)
            n2 = 0;
        const result = parseFloat(n1) + parseFloat(n2);
        return result;
    }

    function canContinue() {
        let fields = {
            name: state.rodac.name,
            type: state.rodac.car_type_id,
            type_coverage: state.typeCoverage.typeCoverageName.type_coverage,
            car_type_select: state.rodac.car_type_select,
            condition: state.rodac.car_status_id,
            model_year: state.rodac.model_id,
            brand: state.rodac.brand,
            model_name: state.rodac.type,
            model_version: state.rodac.description_id,
            zip: state.rodac.zip,
            colonia: state.rodac.colonia,
            municipio: state.rodac.municipio,
            ciudad: state.rodac.ciudad,
            estado: state.rodac.estado,
            amount: state.rodac.amount,
            estado_placa: state.rodac.estado_placa,
            plazo: state.rodac.payment_configuration_id,
            enganche: state.rodac.enganche,
            type_engine: state.typeEngine.typeEngineName.type_engine,
            typeRegime: state.typeRegime.typeRegimeName.name,
            residualValue: state.residualValue,
            insurance: state.rodac.insurance,
        };

        if (isCotizador3) {
            delete fields.type_coverage;
            delete fields.typeRegime;
            delete fields.residualValue;
        }

        let arrayAux = [];
        for (let key in fields) {
            if (fields[key] === null || fields[key] === "" || fields[key] === undefined) {
                if (state.typeCoverage.typeCoverageName.id == 1 && (key === 'typeRegime' || key === 'residualValue')) {
                    fields[key] = null;
                    continue;
                }

                arrayAux.push(key);
                const existItem = errorsArray.find(item => item === key);
                if (!existItem) arrayAux.push(key);
            }
        }
        if (arrayAux.length > 0) {
            const arraySinDuplicados = arrayAux.filter((item, index, self) =>
                index === self.indexOf(item)
            );
            setErrorsArray(arraySinDuplicados);
            if (state.typeCoverage.typeCoverageName.id === 1 && arraySinDuplicados.includes('typeRegime') && arraySinDuplicados.includes('typeRegime') && arraySinDuplicados.length === 3) {
                return true;
            }
            return false;
        }

        return true;
    }

    function buildSubmitPayload() {
        if (!canContinue()) {
            return null;
        }
        let insurances = {};
        let plazos = state.plazos.parray;
        state.rodac.plazosInsurancesFiltered.filter(Boolean).forEach(({ id, amount, logo }, i) => {
            insurances[plazos[i]] = {
                insurance: amount,
                insurance_id: id,
                company: logo,
            }
        })
        let params = {
            name: state.rodac.name,
            car_type: state.rodac.car_types.find(item => +item.id === +state.rodac.car_type_id)?.type,
            car_status: state.rodac.car_statuses.find(item => +item.id === +state.rodac.car_status_id)?.status,
            year: state.rodac.model_id,
            brand: state.rodac.brand,
            type: state.rodac.type,
            car_id: state.rodac.description_id,
            car: state.rodac.descriptions.find(item => item.id === state.rodac.description_id)?.description,
            price: +state.rodac.amount,
            accesories: +state.rodac.accesorios,
            term: +state.rodac.payment_configurations.find(item => +item.id === +state.rodac.payment_configuration_id)?.configuration,
            terms: state.rodac.payment_configurations,
            down_payment: +state.rodac.enganche,
            license_state: state.rodac.estado_placa.id,
            insurance_id: state.rodac.insurance,
            insurance: state.rodac.insurances.find(item => +item.id === +state.rodac.insurance)?.amount,
            date: state.rodac.date,
            insurances: insurances,
            zip: state.rodac.zip,
            city: state.rodac.ciudad,
            user_state: state.rodac.estado,
            colony: state.rodac.colonia,
            delegation: state.rodac.municipio,
            typeCoverage: state.typeCoverage.typeCoverageName,
            typeEngine: state.typeEngine,
            budget_id: budgetId,
            secret: budgetSecret,
        }
        let params2 = {
            name: state.rodac.name,
            car_type: state.rodac.car_types.find(item => +item.id === +state.rodac.car_type_id)?.type,
            car_status: state.rodac.car_statuses.find(item => +item.id === +state.rodac.car_status_id)?.status,
            year: state.rodac.model_id,
            brand: state.rodac.brand,
            type: state.rodac.type,
            car_id: state.rodac.description_id,
            car: state.rodac.descriptions.find(item => item.id === state.rodac.description_id)?.description,
            price: +state.rodac.amount,
            accesories: +state.rodac.accesorios,
            term: +state.rodac.payment_configurations.find(item => +item.id === +state.rodac.payment_configuration_id)?.configuration,
            terms: state.rodac.payment_configurations,
            down_payment: +state.rodac.enganche,
            license_state: state.rodac.estado_placa.id,
            insurance_id: state.rodac.insurance,
            insurance: state.rodac.insurances.find(item => +item.id === +state.rodac.insurance)?.amount,
            date: state.rodac.date,
            insurances: insurances,
            zip: state.rodac.zip,
            city: state.rodac.ciudad,
            user_state: state.rodac.estado,
            colony: state.rodac.colonia,
            delegation: state.rodac.municipio,
            typeRegime: state.typeRegime.typeRegimeName,
            typeCoverage: state.typeCoverage.typeCoverageName,
            amountVehicle: state.rodac.amount,
            typeEngine: state.typeEngine,
            expectedKm: state.expectedKm,
            residualValue: state.residualValue,
            plazoVal,
            budget_id: budgetId,
            secret: budgetSecret,
        }
        const isCotizador2 = state.typeCoverage.typeCoverageName.id === 2;
        const suffix = true ? '-v2' : '';

        return {
            endpoint: submitEndpointOverride || ((isCotizador2 ? 'cotizador2' : 'cotizador') + suffix),
            payload: isCotizador2 ? params2 : params,
        };
    }

    function submit(extraPayload = {}) {
        const built = buildSubmitPayload();
        if (!built || loading) {
            return Promise.reject('Cannot continue or already loading')
        }

        setLoading(true);

        // Solo aceptar overrides explícitos (objeto plano), nunca un evento de React de un onClick
        const extra = (extraPayload && typeof extraPayload === 'object'
            && !extraPayload.nativeEvent && !extraPayload.target)
            ? extraPayload
            : {};

        const urlCotizador = built.endpoint;
        // Mandar bearer token siempre que el usuario tenga sesión activa
        // (admin/vendedor o cliente registrado). El backend filtra qué hacer
        // con el user en assignCreatorAndAdvisor() según el rol.
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const sourceTrimAccess = isCotizador3 ? getSourceTrimAccess() : null;
        const payload = sourceTrimAccess?.token
            ? { ...built.payload, source_trim_access_token: sourceTrimAccess.token, ...extra }
            : { ...built.payload, ...extra };

        return axios.post(
            `${url}/${urlCotizador}`,
            payload,
            { headers }
        ).then(res => {
            if (res.data.error) {
                openModal({ message: res.data.error, time: 180 * 1000 });
                throw new Error(res.data.error);
            } else {
                setState(prevState => ({
                    ...prevState,
                    ...res.data
                }))
                internalVariables.current.secret = res.data.budget_secret;
                internalVariables.current.budget_id = res.data.budget_id;

                // El modal con las 3 cards se abre desde el CTA flotante en QuoteResultsV2.
                return res.data;
            }
        }).catch(err => {
            if (isCotizador3 && err?.response?.status === 403) {
                clearSourceTrimAccess();
                notifySourceTrimAccessInvalid();
            }
            const msg = err?.response?.data?.error;
            if (msg) {
                openModal({ message: msg, time: 10000 });
            }
            throw err;
        }).finally(() => {
            setLoading(false)
        })
    }

    function loadInsurances() {
        if (state.rodac.description_id !== null && state.rodac.car_type_id !== null && state.rodac.car_status_id !== null && state.rodac.compensation_id !== null &&
            state.rodac.amount !== null && state.rodac.zip !== null &&
            state.rodac.use_type_id !== null && state.rodac.deductible_id !== null && state.rodac.payment_configuration_id !== null &&
            state.rodac.package_id !== null &&
            state.rodac.colonia !== null && state.rodac.municipio !== null && state.rodac.ciudad !== null && state.rodac.estado !== null &&
            state.rodac.subchannel_id !== null && !state.rodac.fetched_insurances) {
            setState(prevState => ({
                ...prevState,
                rodac: {
                    ...prevState.rodac,
                    fetch_insurances: true,
                    fetched_insurances: false,
                }
            }))
        }
    }

    const handleAutoCompleteAddress = async (value) => {
        const data = value
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(async () => {
            if (data === null || data === undefined || data.length === 0) return;
            setLoading(true);
            const res = await getZipCode(data);

            if (res === null) {
                setState(prevState => ({
                    ...prevState,
                    rodac: {
                        ...prevState.rodac,
                        estado: '',
                        municipio: '',
                        zip: data,
                        colonia: '',
                    }
                }));
                setColoniesFiltered([]);
                setLoading(false);
                return;
            } else {
                setLoading(false);
                setColoniesFiltered(
                    res.data.map((item, index) => ({
                        id: index + 1,
                        colonia: item.colonia.toUpperCase(),
                        label: item.colonia.toUpperCase(),
                    }))
                );
                let edo = res.data[0].estado === 'CDMX' ? 'CIUDAD DE MÉXICO' : res.data[0].estado
                edo = edo.toUpperCase()
                const muni = res.data[0].municipio
                setState(prevState => ({
                    ...prevState,
                    rodac: {
                        ...prevState.rodac,
                        estado: edo,
                        municipio: muni,
                        zip: data,
                        ciudad: edo,
                        colonia: '',
                    }
                }));
            }
        }, 800);
    };

    const hadleResidualValue = (value) => {
        const number = state.rodac.payment_configuration_id;
        let result = residualValue;
        if (!result) {
            return []
        }
        if (number === 16) {
            result = residualValue.filter(item => parseInt(item.percentage) <= 20)
            result = result.map(item => ({ id: item.id, percentage: `${item.percentage}%` }))
        } else if (number === 15) {
            result = residualValue.filter(item => parseInt(item.percentage) <= 35)
            result = result.map(item => ({ id: item.id, percentage: `${item.percentage}%` }))
        } else if (number === 14) {
            result = residualValue.filter(item => parseInt(item.percentage) <= 45)
            result = result.map(item => ({ id: item.id, percentage: `${item.percentage}%` }))
        } else if (number === 13) {
            result = residualValue.filter(item => parseInt(item.percentage) <= 50)
            result = result.map(item => ({ id: item.id, percentage: `${item.percentage}%` }))
        }
        return result
    }

    const cleanErrors = (name) => {
        setErrorsArray(errorsArray.filter(item => item !== name))
    }

    const setClass = (name) => {
        return errorsArray.includes(name)
    }

    const handleStatePlateChange = (plateState) => {
        const defaults = getAddressForStatePlate(plateState);
        const nextColonies = defaults?.colonia
            ? [buildColonyOption(defaults.colonia, plateState?.id)]
            : [];

        setColoniesFiltered(nextColonies);
        cleanErrors('estado_placa');

        setState(prevState => ({
            ...prevState,
            rodac: {
                ...prevState.rodac,
                estado_placa: plateState,
                zip: defaults?.zip ?? null,
                colonia: defaults?.colonia ?? null,
                municipio: defaults?.municipio ?? null,
                ciudad: defaults?.ciudad ?? null,
                estado: defaults?.estado ?? null,
                fetched_insurances: false,
                insurance: null,
                insurances: [],
                plazosInsurances: []
            }
        }));
    }

    // IMPORTANT: Set typeCoverage from URL parameter FIRST, before any other useEffect
    // This ensures the URL param is captured and not overwritten
    useEffect(() => {
        const { status } = urlParams;
        if (status && !urlParamLoaded.current) {
            const foundType = type_coverage_options.find(item => item.type_coverage.toLowerCase() === status.toLowerCase());
            if (foundType) {
                urlParamLoaded.current = true;
                setState(prevState => ({
                    ...prevState,
                    typeCoverage: {
                        typeCoverage: foundType.type_coverage,
                        typeCoverageName: foundType,
                        id: foundType.id
                    }
                }));
            }
        }
    }, []); // Run only once on mount

    useEffect(() => {
        // Step 1: Set initial simple data and kick off the vehicle data loading chain.
        if (budgetData && budgetData !== previousBudgetData.current && stateAmple && typeRegime && residualValue && state.rodac.payment_configurations.length > 0 && state.rodac.car_types.length > 0) {
            // Si solo cambiaron valores financieros (tasa, commission, gps), no resetear datos del vehículo
            const prev = previousBudgetData.current;
            if (prev && prev.vehicle === budgetData.vehicle
                && prev.price === budgetData.price
                && prev.term === budgetData.term
                && prev.down_payment === budgetData.down_payment
                && prev.vehicule_type === budgetData.vehicule_type
                && prev.state_plate_id === budgetData.state_plate_id
                && prev.engine === budgetData.engine
                && prev.zip === budgetData.zip
                && prev.accesories === budgetData.accesories
            ) {
                // Solo actualizar la referencia sin resetear el estado
                previousBudgetData.current = budgetData;
                return;
            }
            previousBudgetData.current = budgetData;
            const foundStatePlate = stateAmple.find(e => e.state.toLowerCase() === budgetData.state_plate_id.toLowerCase());
            const statePlateDefaults = getAddressForStatePlate(foundStatePlate);
            const foundPaymentConfig = state.rodac.payment_configurations.find(p => +p.configuration === +budgetData.term);
            const carStatusId = budgetData.vehicule_type.toLowerCase() === 'nuevo' ? 1 : 2;
            const carStatusSelect = { id: carStatusId, status: budgetData.vehicule_type.toUpperCase() };
            let foundCarType = state.rodac.car_types.find(ct => ct.type.toLowerCase() === budgetData.vehicle.typeAutoOrPickup.toLowerCase());
            if (!foundCarType) {
                foundCarType = {
                    'id': 1,
                    'type': 'AUTO'
                }
            }
            // Find the default regime and create a clean object for the state
            const foundRegimeFromApi = typeRegime.find(r => r.id === 2);
            let regimeObjectForState = null;
            if (foundRegimeFromApi) {
                regimeObjectForState = { id: foundRegimeFromApi.id, name: foundRegimeFromApi.name };
            }

            // Prepare typeCoverage update - only if not already loaded from URL.
            // Detect from budgetData.financial_details.typeBudget: 'finance' => FINANCIERO (1), 'pure' => PURO (2)
            const detectedTypeBudget = budgetData.financial_details?.typeBudget;
            const detectedCoverage = detectedTypeBudget === 'finance'
                ? { id: 1, type_coverage: 'FINANCIERO' }
                : { id: 2, type_coverage: 'PURO' };
            const typeCoverageUpdate = urlParamLoaded.current
                ? {} // Don't override if URL param was already loaded
                : {
                    typeCoverage: {
                        typeCoverage: detectedCoverage.type_coverage,
                        typeCoverageName: detectedCoverage,
                        id: detectedCoverage.id,
                    }
                };
            setState(prevState => ({
                ...prevState,
                ...typeCoverageUpdate, // Only set if URL param wasn't loaded
                typeEngine: {
                    typeEngineName: budgetData.engine.toLowerCase().includes('gasolina')
                        ? { id: 1, type_engine: 'Gasolina/Diesel' }
                        : { id: 2, type_engine: 'Híbrido/Eléctrico' },
                    id: budgetData.engine.toLowerCase().includes('gasolina') ? 1 : 2,
                },
                typeRegime: {
                    id: 2,
                    typeRegimeName: regimeObjectForState || { id: 2 },
                },
                residualValue: {
                    id: budgetData.residual_value_id?.id || 3,
                    percentage: parseInt(budgetData.residual_value_id?.percentage) || 5
                },
                rodac: {
                    ...prevState.rodac,
                    // Set simple fields
                    name: budgetData.name,
                    amount: budgetData.price,
                    accesorios: budgetData.accesories,
                    enganche: budgetData.down_payment,
                    zip: budgetData.zip || statePlateDefaults?.zip || null,
                    colonia: normalizeAddressValue(budgetData.colony) || statePlateDefaults?.colonia || null,
                    municipio: normalizeAddressValue(budgetData.delegation || budgetData.city) || statePlateDefaults?.municipio || null,
                    ciudad: normalizeAddressValue(budgetData.city) || statePlateDefaults?.ciudad || null,
                    estado: normalizeAddressValue(budgetData.user_state) || statePlateDefaults?.estado || null,
                    estado_placa: foundStatePlate || null,
                    payment_configuration_id: foundPaymentConfig ? foundPaymentConfig.id : prevState.rodac.payment_configuration_id,

                    // --- Vehicle Chain ---
                    // 1. Set the FIRST values to kick off the chain
                    car_status_id: carStatusId,
                    car_status_select: carStatusSelect,
                    car_type_id: foundCarType ? foundCarType.id : null,
                    car_type_select: foundCarType || null,

                    // Set compensation based on car status (Nuevo=1 Valor Factura, Seminuevo=4 Valor Comercial)
                    compensations: [
                        { id: 1, type: "Valor Factura" },
                        { id: 4, type: "Valor Comercial" }
                    ],
                    compensation_id: carStatusId === 1 ? 1 : 4,

                    // 2. RESET the rest of the chain to mimic manual selection
                    model_id: null,
                    model_select: null,
                    models: [],
                    brand: null,
                    brand_select: null,
                    brands: [],
                    type: null,
                    type_select: null,
                    types: [],
                    description_id: null,
                    description_select: null,
                    descriptions: [],
                }
            }));

            setPlazoVal(budgetData.term);
        }
    }, [budgetData, stateAmple, typeRegime, residualValue, state.rodac.payment_configurations, state.rodac.car_types]);

    useEffect(() => {
        const defaults = getAddressForStatePlate(state.rodac.estado_placa);

        if (!defaults) {
            return;
        }

        const colonyOption = buildColonyOption(state.rodac.colonia || defaults.colonia, state.rodac.estado_placa?.id);

        if (colonyOption) {
            setColoniesFiltered(prev =>
                prev.length === 1 && prev[0].colonia === colonyOption.colonia
                    ? prev
                    : [colonyOption]
            );
        }

        const missingAddress =
            !state.rodac.zip ||
            !state.rodac.colonia ||
            !state.rodac.municipio ||
            !state.rodac.ciudad ||
            !state.rodac.estado;

        if (!missingAddress) {
            return;
        }

        setState(prevState => ({
            ...prevState,
            rodac: {
                ...prevState.rodac,
                zip: prevState.rodac.zip || defaults.zip,
                colonia: prevState.rodac.colonia || defaults.colonia,
                municipio: prevState.rodac.municipio || defaults.municipio,
                ciudad: prevState.rodac.ciudad || defaults.ciudad,
                estado: prevState.rodac.estado || defaults.estado,
            }
        }));
    }, [state.rodac.estado_placa]);

    useEffect(() => {
        if (state.rodac.payment_configurations.length < 1)
            return;
        else {
            try {
                let targetId = state.rodac.payment_configuration_id;
                let obj_arr = state.rodac.payment_configurations;
                let index = obj_arr.findIndex(obj => obj.id === targetId);

                if (index !== -1) {
                    const nextPlazos = plazosFor(obj_arr);
                    setTimeout(() => {
                        setState(prevState => ({
                            ...prevState,
                            plazos: {
                                ...nextPlazos,
                                currentIndex: index,
                            }
                        }))
                    }, 300)
                }
            } catch (error) {
                console.log(error)
            }
        }

        // Auto-cap the selected residual if it exceeds the new term's limit
        try {
            const RESIDUAL_LIMIT_BY_CONFIG = { 13: 50, 14: 45, 15: 35, 16: 20 };
            const limit = RESIDUAL_LIMIT_BY_CONFIG[state.rodac.payment_configuration_id];
            const currentPct = parseInt(state.residualValue?.percentage);
            if (limit && residualValue && Number.isFinite(currentPct) && currentPct > limit) {
                const eligible = residualValue
                    .filter(item => parseInt(item.percentage) <= limit)
                    .sort((a, b) => parseInt(b.percentage) - parseInt(a.percentage));
                if (eligible.length > 0) {
                    const next = eligible[0];
                    setState(prevState => ({
                        ...prevState,
                        residualValue: {
                            id: next.id,
                            percentage: next.percentage,
                        }
                    }));
                }
            }
        } catch (error) {
            console.log(error);
        }
    }, [state.rodac.payment_configuration_id]);

    useEffect(() => {
        let actualInsuranceId = state.rodac.insurance;
        if (actualInsuranceId == null && state.rodac.plazosInsurancesFiltered.length > 0) {
            setState(prevState => ({
                ...prevState,
                rodac: {
                    ...prevState.rodac,
                    plazosInsurancesFiltered: [],
                    plazosInsurancesChanged: []
                }
            }))
        } else if (actualInsuranceId !== null) {
            let currentSelected = { ...state.rodac.insurances.find(item => +item.id === +actualInsuranceId) };
            let logo = currentSelected.logo;
            let filtered;
            let changed = [];
            let unfiltered = state.rodac.plazosInsurances;

            filtered = unfiltered.map(arr => {
                let res = arr.find(item => item.logo === logo);
                if (res) {
                    changed.push(false)
                    return { ...res };
                }
                else {
                    changed.push(true);
                    return getObjectWithLowestValue(arr, "amount");
                }
            })

            setState(prevState => ({
                ...prevState,
                rodac: {
                    ...prevState.rodac,
                    plazosInsurancesFiltered: [...filtered],
                    plazosInsurancesChanged: [...changed]
                }
            }))
        }
    }, [state.rodac.insurance]);

    useEffect(() => {
        if (state.rodac.subchannels.length === 0 && state.rodac.fetch_subchannels) {
            getSubchannels(state, setState);
        }
    }, [state.rodac.subchannels]);

    useEffect(() => {
        /* debugger   */
        if (budgetData && id) {
            getSubchannels(state, setState);
        }
    }, [budgetData, id]);

    useEffect(() => {
        axios.get(`${api}/getStaticData`)
            .then(res => {
                setTypeRegime(res.data.regimes);
                setResidualValue(res.data.residualValue)
                setStateAmple(res.data.state)
                setPercentageDownPayment({
                    min: parseFloat(res.data.downPayment.min),
                    max: parseFloat(res.data.downPayment.max)
                })
            })
            .catch(err => console.log(err))
    }, []);

    useEffect(() => {
        if (residualValue && !state.residualValue) {
            setState(prev => ({
                ...prev,
                residualValue: residualValue.find(value => value.percentage === "5")
            }));
        }
        if (
            residualValue &&
            state.residualValue &&
            (
                state.residualValue.id == null ||           // null o undefined
                state.residualValue.percentage == null      // null o undefined
            )
        ) {
            state.residualValue = {
                id: state.residualValue.id ?? 3,            // si está null/undefined → 3
                percentage: state.residualValue.percentage ?? 5, // si está null/undefined → 5
            };
        }
    }, [residualValue]);

    useEffect(() => {
        if (state.rodac.amount !== null) {
            setState(prevState => ({
                ...prevState,
                rodac: {
                    ...prevState.rodac,
                    // Monto precargado (budgetData) se respeta tal cual; solo el valor por
                    // defecto sale del mínimo en pesos (redondeado hacia arriba al peso).
                    enganche: budgetData && budgetData.down_payment
                        ? budgetData.down_payment
                        : limitesPagoInicial({
                            base: sumAmountAndAccesories(),
                            minPct: percentageDownPayment.min,
                            maxPct: percentageDownPayment.max,
                        }).min,
                },
            }));
        }
    }, [state.rodac.amount, state.rodac.accesorios]);


    useEffect(() => {
        if (Object.keys(urlParams).length < 1) return;
        else if (
            translateCarStatus("nuevo").id !== null &&
            state.rodac.car_status_id === null &&
            minimumLoaded
        ) {
            setTimeout(() => {
                setState(prevState => ({
                    ...prevState,
                    rodac: {
                        ...prevState.rodac,
                        car_status_select: translateCarStatus("nuevo").select,
                        car_status_id: translateCarStatus("nuevo").id,
                    },
                }));
            }, 200);

            //getModels(state, setState)
        }
    }, [minimumLoaded]);

    // UseEffect para quitar el secret (si existe) en caso 
    // de que se restablezcan los seguros. 
    useEffect(() => {
        if (internalVariables.current.secret && state.rodac.insurances.length == 0) {
            internalVariables.current.secret = null;
            internalVariables.current.budget_id = null;
        }
    }, [state.rodac.insurances]);

    useEffect(() => {
        if (minimumLoaded) {
            return;
        } else {
            let arr = [
                state.rodac.subchannels,
                state.rodac.car_types,
                state.rodac.car_statuses,
                state.rodac.use_types,
                state.rodac.deductibles,
                state.rodac.policy_types,
                state.rodac.packages,
                state.rodac.payment_configurations,
            ];

            for (let i = 0; i < arr.length; i++) {
                if (arr[i].length < 1) return;
                else if (i === arr.length - 1) setMinimumLoaded(true);
            }
        }
    }, [state]);

    useEffect(() => {
        if (state.rodac.subchannel_id !== null && state.rodac.fetch_car_types) {
            getCarTypes(state, setState);
        }
    }, [state.rodac.subchannel_id]);

    useEffect(() => {
        if (state.rodac.car_types.length > 0 && state.rodac.fetch_car_statuses) {
            getCarStatuses(state, setState);
        }
    }, [state.rodac.car_types]);

    useEffect(() => {
        if (state.rodac.car_statuses.length > 0 && state.rodac.fetch_use_types) {
            getUseTypes(state, setState);
        }
    }, [state.rodac.car_statuses]);

    useEffect(() => {
        if (state.rodac.use_types.length > 0 && state.rodac.fetch_deductibles) {
            getDeductibles(state, setState);
        }
    }, [state.rodac.use_types]);

    useEffect(() => {
        if (state.rodac.deductibles.length > 0 && state.rodac.fetch_policy_types) {
            getPolicyTypes(state, setState);
        }
    }, [state.rodac.deductibles]);

    useEffect(() => {
        if (state.rodac.policy_types.length > 0 && state.rodac.fetch_packages) {
            getPackages(state, setState);
        }
    }, [state.rodac.policy_types]);

    useEffect(() => {
        if (
            state.rodac.packages.length > 0 &&
            state.rodac.fetch_payment_configurations
        ) {
            getPaymentConfiguration(state, setState);
        }
    }, [state.rodac.packages]);

    useEffect(() => {
        if (
            state.rodac.payment_configurations.length > 0 &&
            state.rodac.fetch_udis
        ) {
            getUdis(state, setState);
        }
    }, [state.rodac.payment_configurations]);

    useEffect(() => {
        if (state.rodac.car_status_id !== null) {
            getCompensations(state, setState);
        }
    }, [state.rodac.car_status_id]);

    useEffect(() => {
        if (state.rodac.compensation_id !== null) {
            getModels(state, setState);
        }
    }, [state.rodac.compensation_id]);

    useEffect(() => {
        if (state.rodac.car_type_id !== null && state.rodac.model_id !== null) {
            getBrands(state, setState);
        }
    }, [state.rodac.car_type_id, state.rodac.model_id]);

    useEffect(() => {
        if (state.rodac.car_type_id !== null && state.rodac.model_id !== null) {
            getBrands(state, setState);
        }
    }, [state.rodac.car_type_id, state.rodac.model_id]);

    useEffect(() => {
        if (
            state.rodac.car_type_id !== null &&
            state.rodac.model_id !== null &&
            state.rodac.brand !== null
        ) {
            getTypes(state, setState);
        }
    }, [state.rodac.car_type_id, state.rodac.model_id, state.rodac.brand]);

    useEffect(() => {
        if (
            state.rodac.car_type_id !== null &&
            state.rodac.model_id !== null &&
            state.rodac.brand !== null &&
            state.rodac.type !== null
        ) {
            getDescriptions(state, setState);
        }
    }, [
        state.rodac.car_type_id,
        state.rodac.model_id,
        state.rodac.brand,
        state.rodac.type,
    ]);

    useEffect(() => {
        if (
            state.rodac.description_id !== null &&
            state.rodac.car_type_id !== null &&
            state.rodac.car_status_id !== null &&
            state.rodac.compensation_id !== null &&
            state.rodac.amount !== null &&
            state.rodac.zip !== null &&
            state.rodac.use_type_id !== null &&
            state.rodac.deductible_id !== null &&
            state.rodac.payment_configuration_id !== null &&
            state.rodac.package_id !== null &&
            state.rodac.colonia !== null &&
            state.rodac.municipio !== null &&
            state.rodac.ciudad !== null &&
            state.rodac.estado !== null &&
            state.rodac.subchannel_id !== null &&
            state.rodac.fetch_insurances
        ) {
            getInsurances(state, setState, state.plazos);
        }
    }, [
        state.rodac.description_id,
        state.rodac.car_type_id,
        state.rodac.car_status_id,
        state.rodac.compensation_id,
        state.rodac.amount,
        state.rodac.zip,
        state.rodac.use_type_id,
        state.rodac.deductible_id,
        state.rodac.payment_configuration_id,
        state.rodac.package_id,
        state.rodac.colonia,
        state.rodac.municipio,
        state.rodac.ciudad,
        state.rodac.estado,
        state.rodac.subchannel_id,
        state.rodac.fetch_insurances,
    ]);

    useEffect(() => {
        if (budgetData && state.rodac.models.length > 0) {
            const foundModel = state.rodac.models.find(m => m.model == budgetData.vehicle.year);
            if (foundModel && state.rodac.model_id !== foundModel.id) {
                setState(prevState => ({
                    ...prevState,
                    rodac: {
                        ...prevState.rodac,
                        model_id: foundModel.id,
                        model_select: foundModel,
                    }
                }));
            }
        }
    }, [budgetData, state.rodac.models]);

    useEffect(() => {
        if (budgetData && state.rodac.brands.length > 0) {
            const foundBrand = state.rodac.brands.find(b => b.brand.toLowerCase() === budgetData.vehicle.brand.toLowerCase());
            if (foundBrand && state.rodac.brand !== foundBrand.brand) {
                setState(prevState => ({
                    ...prevState,
                    rodac: {
                        ...prevState.rodac,
                        brand: foundBrand.brand,
                        brand_select: foundBrand,
                    }
                }));
            }
        }
    }, [budgetData, state.rodac.brands]);

    useEffect(() => {
        if (budgetData && state.rodac.types.length > 0) {
            const foundType = state.rodac.types.find(t => t.type.toLowerCase() === budgetData.vehicle.model.toLowerCase());
            if (foundType && state.rodac.type !== foundType.type) {
                setState(prevState => ({
                    ...prevState,
                    rodac: {
                        ...prevState.rodac,
                        type: foundType.type,
                        type_select: foundType,
                    }
                }));
            }
        }
    }, [budgetData, state.rodac.types]);

    useEffect(() => {
        if (budgetData && state.rodac.descriptions.length > 0) {
            const foundDescription = state.rodac.descriptions.find(d => d.description.toLowerCase() === budgetData.vehicle.description.toLowerCase());
            if (foundDescription && state.rodac.description_id !== foundDescription.id) {
                setState(prevState => ({
                    ...prevState,
                    rodac: {
                        ...prevState.rodac,
                        description_id: foundDescription.id,
                        description_select: foundDescription,
                    }
                }));
            }
        }
    }, [budgetData, state.rodac.descriptions]);


    return {
        calculatorState: state,
        setState,
        loading,
        plazoVal,
        setPlazoVal,
        plazoTimer,
        errorsArray,
        canContinue,
        submit,
        buildSubmitPayload,
        loadInsurances,
        cleanErrors,
        setClass,
        sumAmountAndAccesories,
        handleAutoCompleteAddress,
        coloniesFiltered,
        dropdownRef,
        typeRegime,
        residualValue,
        stateAmple,
        percentageDownPayment,
        hadleResidualValue,
        type_coverage: type_coverage_options,
        handleStatePlateChange,
        showManualAddressFields: false,
    };
}
